import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { MessageService } from 'services';
import NetworkService from 'services/networkService';
import { getError } from 'utils/helpers';
import i18n from 'services/i18n';
import { fileExtensions } from 'constants/mapping';
import { AppConstants } from 'constants/index';
import { ErrorMessage } from 'app/settings/types';
import { getFileNameAndExtension, getFileSize, isJSON } from 'utils/utils';
import { DirectoryRule } from 'app/resource/types/fileManager';
import { AdminEditorWrapper, AdminEditorLoader } from './AdminEditor.styled';
import { Tags } from './types';

const { png, jpg, jpeg } = fileExtensions;

const accept = [png, jpg, jpeg];
const maxFileSize = AppConstants.fileSizeLimits.maxBackgroundSize;
type TinyMCEEditor = Parameters<NonNullable<Editor['props']['onInit']>>[1];

 interface TinyMCEMenuItem {
  type: string;
  text?: string;
  icon?: string;
  onAction?: () => void;
}
type TinyMCECallback = (items: TinyMCEMenuItem[]) => void;

export interface AdminEditorProps {
  changeLoadingState?: (loading: boolean) => void;
  textMode?: boolean;
  editorChange?: (content: string) => void;
  setErrorMessage?: (error: ErrorMessage) => void;
  value?: string;
  tags?: Tags;
  height?: string;
  directoryPath?: string;
  noToolbar?: boolean;
  readOnly?: boolean;
  initialValue?: string;
  plugins?: string[];
  directoryRules?: DirectoryRule[];
  brandId?: number;
  isNightMode?: boolean;
}

const AdminEditor = ({
  editorChange,
  value,
  tags,
  textMode = false,
  height = '500',
  setErrorMessage,
  directoryPath = 'messages',
  noToolbar = false,
  changeLoadingState,
  readOnly = false,
  initialValue,
  plugins = ['autolink', 'codesample', 'fullscreen', 'image', 'lists', 'link', 'searchreplace', 'table', 'visualblocks', 'wordcount', 'code', 'preview'],
  brandId,
  directoryRules,
  isNightMode = false,
}: AdminEditorProps) => {
  const { t } = useTranslation();
  const { language } = i18n;

  const [key, setKey] = useState(0);

  const nightModeConf = useMemo(
    () => (isNightMode
      ? {
        skin: 'oxide-dark',
        content_css: 'dark',
      }
      : {}),
    [isNightMode],
  );

  const generateTagsMenu = useCallback(
    (editor: TinyMCEEditor) => tags?.map((el) => ({
      type: 'menuitem',
      text: el.key,
      icon: 'copy',
      onAction() {
        return editor.insertContent(el.key);
      },
    })) ?? [],
    [tags],
  );

  const handleUpload = useCallback(
    (file: File) => {
      changeLoadingState?.(true);
      const formData = new FormData();
      if (brandId) {
        formData.append('brandId', brandId.toString());
      }
      formData.append('file', file);
      formData.append('directoryPath', directoryPath);
      formData.append('generateNewFileName', 'true');
      const options = {
        data: formData,
        headers: { 'content-type': 'multipart/form-data' },
      };
      const { BrandAdmin, FileManager, UploadFile } = AppConstants.api;
      return NetworkService.makeAPIPostRequest([BrandAdmin, FileManager, UploadFile], options);
    },
    [brandId, changeLoadingState, directoryPath],
  );

  const defaultToolbar = textMode
    ? 'undo redo '
    : `a11ycheck undo redo | bold italic | forecolor backcolor |
     template | alignleft aligncenter alignright alignjustify
       | bullist numlist | link image tinydrive code | table `;

  const toolbar = tags?.length ? defaultToolbar.concat('insertfield') : defaultToolbar;
  const noToolbarConfig = useMemo(
    () => (noToolbar
      ? {
        menubar: false,
        toolbar: false,
      }
      : {}),
    [noToolbar],
  );

  const getBaseConfig = useCallback(
    () => ({
      width: '100%',
      height,
      resize: false,
      branding: false,
      promotion: false,
      visual: false,
      language,
      statusbar: false,
      language_url: `/tinymce/langs/${language}.js`,
      menu: {
        file: { title: 'File', items: 'newdocument  | fullscreen ' },
        edit: { title: 'Edit', items: 'undo redo | cut copy | selectall | searchreplace' },
        view: { title: 'View', items: 'preview | visualchars visualblocks' },
        insert: { title: 'Insert', items: '' },
        format: { title: 'Format', items: 'underline strikethrough superscript subscript codeformat | formats blockformats fontformats fontsizes | removeformat' },
        tools: { title: 'Tools', items: '' },
        table: { title: 'Table', items: '' },
        help: { title: 'Help', items: 'help' },
      },
      autosave_ask_before_unload: false,
      powerpaste_allow_local_images: true,
      plugins,
      custom_elements: 'style,link,~link',
      image_title: true,
      automatic_uploads: false,
      file_picker_types: 'image',
      file_picker_callback(cb: (url: string, meta?: { title: string }) => void) {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');

        input.onchange = (e) => {
          const { files } = e.target as HTMLInputElement;
          if (files?.length) {
            const file = files?.[0];
            const [, extension] = getFileNameAndExtension(file.name);
            const ext = `.${extension}`;
            if (!(accept as string[]).includes(ext) || (brandId && !directoryRules?.find((el) => el.permittedFileTypeExtension === ext))) {
              MessageService.error(t('notification:wrongFileFormat'));
              return false;
            }

            const rule = brandId && directoryRules?.find((el) => (_.isNull(el.permittedFileTypeExtension) || el.permittedFileTypeExtension === ext) && !_.isNull(el.maxFileSize));

            const allowedMaxFileSize = rule ? rule.maxFileSize : null;

            const allowedFileSize = !_.isNull(allowedMaxFileSize) ? Math.min(maxFileSize, allowedMaxFileSize) : maxFileSize;

            if (file.size > allowedFileSize) {
              MessageService.error(`${t('maxUploadSize')} ${getFileSize(allowedFileSize)}`);
              return false;
            }

            handleUpload(file)
              .then(({ data }) => {
                changeLoadingState?.(false);
                const {
                  fileInfo: { cdnUrl },
                } = data;
                const reader = new FileReader();
                reader.onload = () => {
                  cb(cdnUrl, { title: file.name });
                };
                reader.readAsDataURL(file);
              })
              .catch((err) => {
                changeLoadingState?.(false);
                if (err && !isJSON(err.message)) {
                  const error = getError(err) as ErrorMessage;
                  setErrorMessage?.(error);
                }
              });
          }
        };

        input.click();
      },
      templates: [],
      toolbar,
      spellchecker_dialog: true,
      setup(editor: TinyMCEEditor) {
        editor.ui.registry.addMenuButton('insertfield', {
          icon: 'code-sample',
          text: t('tags'),
          fetch(callback: TinyMCECallback) {
            callback(generateTagsMenu(editor));
          },
        });
      },
      ...noToolbarConfig,
      ...nightModeConf,
    }),
    [height, language, plugins, toolbar, noToolbarConfig, nightModeConf, brandId, directoryRules, handleUpload, t, changeLoadingState, setErrorMessage, generateTagsMenu],
  );

  const onlyTextConfig = {
    ...getBaseConfig(),
    plugins: [],
    toolbar,
    context_menu: false,
    menubar: false,
  };

  const config = textMode ? onlyTextConfig : getBaseConfig();

  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [language, isNightMode, tags, directoryRules]);

  const handleEditorChange = (content: string) => {
    if (content !== value) {
      editorChange?.(content);
    }
  };

  return (
    <AdminEditorWrapper>
      <AdminEditorLoader indicator={<LoadingOutlined spin />} />
      <Editor disabled={readOnly} key={key} init={{ ...config, ...nightModeConf }} onEditorChange={handleEditorChange} initialValue={initialValue} value={value} />
    </AdminEditorWrapper>
  );
};

export default AdminEditor;
